import { useEffect, useRef, useState } from 'react';
import type { TranscriptMessage } from '@/chat/components/ChatTranscript';

type Connection = 'connecting' | 'connected' | 'disconnected' | 'denied';
interface Delivery {
  message: TranscriptMessage;
  requestId?: string;
}
export function readDelivery(value: unknown): Delivery | null {
  if (!value || typeof value !== 'object') return null;
  const id: unknown = Reflect.get(value, 'id');
  const content: unknown = Reflect.get(value, 'message');
  const timestamp: unknown = Reflect.get(value, 'timestamp');
  const own: unknown = Reflect.get(value, 'is_own');
  const user: unknown = Reflect.get(value, 'user');
  if (
    Reflect.get(value, 'type') !== 'chat_message' ||
    typeof id !== 'string' ||
    !id ||
    typeof content !== 'string' ||
    typeof timestamp !== 'string' ||
    typeof own !== 'boolean' ||
    !user ||
    typeof user !== 'object' ||
    typeof Reflect.get(user, 'id') !== 'number'
  )
    return null;
  const date = new Date(timestamp);
  const author: unknown = Reflect.get(user, 'get_display_name');
  const requestId: unknown = Reflect.get(value, 'request_id');
  if (Number.isNaN(date.getTime()) || typeof author !== 'string') return null;
  return {
    message: { id, content, timestamp: date, own, author },
    requestId: typeof requestId === 'string' ? requestId : undefined,
  };
}
export function useRoomSocket(roomId: string, teamId?: number) {
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [connection, setConnection] = useState<Connection>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<number>(0);
  const socket = useRef<WebSocket | null>(null);
  const pending = useRef<{
    id: string;
    timer: ReturnType<typeof setTimeout>;
    resolve: () => void;
    reject: (error: Error) => void;
  } | null>(null);
  useEffect(() => {
    setMessages([]);
    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryCount = 0;
    const rejectPending = (message: string) => {
      const request = pending.current;
      if (!request) return;
      clearTimeout(request.timer);
      pending.current = null;
      request.reject(new Error(message));
    };
    const connect = () => {
      if (disposed) return;
      setConnection('connecting');
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const scope = teamId == null ? '' : `?team=${teamId}`;
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws/chat/${encodeURIComponent(roomId)}/${scope}`);
      socket.current = ws;
      ws.onopen = () => {
        if (disposed) return;
        setConnection('connected');
        setError(null);
        retryCount = 0;
      };
      ws.onmessage = (event: MessageEvent<string>) => {
        if (disposed || ws !== socket.current) return;
        let data: unknown;
        try {
          data = JSON.parse(event.data);
        } catch {
          setError('The room sent an invalid message.');
          return;
        }
        if (data && typeof data === 'object' && Reflect.get(data, 'type') === 'error') {
          const detail: unknown = Reflect.get(data, 'message');
          const message = typeof detail === 'string' ? detail : 'The message was rejected.';
          setError(message);
          rejectPending(message);
          return;
        }
        const delivery = readDelivery(data);
        if (!delivery) {
          setError('The room sent an invalid message.');
          return;
        }
        setMessages((items) =>
          items.some((item) => item.id === delivery.message.id) ? items : [...items, delivery.message],
        );
        if (delivery.requestId && delivery.requestId === pending.current?.id) {
          clearTimeout(pending.current.timer);
          pending.current.resolve();
          pending.current = null;
        }
      };
      ws.onerror = () => {
        if (!disposed) setError('The room connection failed. Reconnecting…');
      };
      ws.onclose = (event: CloseEvent) => {
        if (disposed || ws !== socket.current) return;
        rejectPending('Delivery was not confirmed. Your draft is retained; check the conversation before retrying.');
        if (event.code === 4400 || event.code === 4401 || event.code === 4403) {
          setConnection('denied');
          setError(event.code === 4401 ? 'Sign in to join this room.' : 'This room is unavailable for the selected team.');
          return;
        }
        setConnection('disconnected');
        if (retryCount < 5) {
          retryTimer = setTimeout(connect, Math.min(1000 * 2 ** retryCount++, 10000));
        } else setError('The room is unavailable. Reconnect to try again.');
      };
    };
    connect();
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      rejectPending('The room connection changed before delivery was confirmed.');
      socket.current?.close();
      socket.current = null;
    };
  }, [roomId, teamId, attempt]);
  const send = (content: string): Promise<void> => {
    const ws = socket.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || pending.current)
      return Promise.reject(new Error('Connect to the room before sending.'));
    setError(null);
    return new Promise<void>((resolve, reject) => {
      const id = crypto.randomUUID();
      const timer = setTimeout(() => {
        pending.current = null;
        reject(
          new Error('Delivery was not confirmed. Your draft is retained; check the conversation before retrying.'),
        );
      }, 10000);
      pending.current = { id, timer, resolve, reject };
      try {
        ws.send(JSON.stringify({ message: content, request_id: id }));
      } catch {
        clearTimeout(timer);
        pending.current = null;
        reject(new Error('Could not send. Your draft is retained.'));
      }
    });
  };
  return { messages, connection, error, send, reconnect: () => setAttempt((value) => value + 1) };
}
